import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { IVacationNfaBuild, IVacationNfaBuildStatusResponse } from "@vmosolutions/oc-interfaces";

import { getVacationNfaBuildStatus, startVacationNfaBuild } from "../helpers";
import { NFA_BUILD_POLL_MS, isNfaBuildRunning } from "./nfaBuildHelpers";

export interface IVacationNfaBuildState {
  /** Null until the first load for this package lands. */
  status: IVacationNfaBuildStatusResponse | null;
  loading: boolean;
  /** True while a start request is in flight. */
  starting: boolean;
  /** True while the latest build runs. The status is re-read every few seconds meanwhile. */
  running: boolean;
  load: () => Promise<void>;
  start: () => Promise<boolean>;
}

/**
 * A package's NFA build: its status, loaded on demand; starting a build; and polling while one
 * runs. `onFinished` is called once for a build this screen saw running, when it comes to an end.
 */
export const useVacationNfaBuild = (
  packageId: string | undefined,
  onFinished: (build: IVacationNfaBuild) => void
): IVacationNfaBuildState => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState<IVacationNfaBuildStatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [starting, setStarting] = useState<boolean>(false);

  // A response for a package the editor has since moved off must not land.
  const packageRef = useRef<string | undefined>(packageId);
  packageRef.current = packageId;
  // Counts every read, so only the newest response is applied; and the loads that own the spinner.
  const readsStartedRef = useRef<number>(0);
  const loadsStartedRef = useRef<number>(0);
  // The build last seen running, so its end is announced once.
  const watchedBuildRef = useRef<string | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const [syncedPackageId, setSyncedPackageId] = useState<string | undefined>(packageId);
  if (packageId !== syncedPackageId) {
    setSyncedPackageId(packageId);
    setStatus(null);
    watchedBuildRef.current = null;
  }

  const apply = useCallback((response: IVacationNfaBuildStatusResponse): void => {
    const build = response.build;
    if (build && isNfaBuildRunning(build)) {
      watchedBuildRef.current = build.buildId;
    } else if (build && watchedBuildRef.current === build.buildId) {
      watchedBuildRef.current = null;
      onFinishedRef.current(build);
    }
    setStatus(response);
  }, []);

  const read = useCallback(
    async (showErrors: boolean): Promise<void> => {
      if (!packageId) return;
      const readNumber = (readsStartedRef.current += 1);
      const response = await getVacationNfaBuildStatus(
        packageId,
        showErrors ? { dispatch, errorMessage: "Unable to load the NFA build" } : null
      );
      if (response && readsStartedRef.current === readNumber && packageRef.current === packageId) apply(response);
    },
    [dispatch, packageId, apply]
  );

  const load = useCallback(async (): Promise<void> => {
    const loadNumber = (loadsStartedRef.current += 1);
    setLoading(true);
    try {
      await read(true);
    } finally {
      if (loadsStartedRef.current === loadNumber) setLoading(false);
    }
  }, [read]);

  const start = useCallback(async (): Promise<boolean> => {
    if (!packageId || starting) return false;
    setStarting(true);
    try {
      const readNumber = (readsStartedRef.current += 1);
      const response = await startVacationNfaBuild(packageId, {
        dispatch,
        infoMessage: "Building NFAs",
        errorMessage: "Unable to start the NFA build",
      });
      if (!response) {
        // Refused - most often because another build is already running, which this then shows.
        void load();
        return false;
      }
      if (packageRef.current !== packageId) return true;
      if (readsStartedRef.current === readNumber) apply(response);
      // A read started meanwhile may have seen the package before the build was recorded, so it is read again.
      else void read(false);
      return true;
    } finally {
      setStarting(false);
    }
  }, [dispatch, packageId, starting, load, read, apply]);

  const running = isNfaBuildRunning(status?.build);

  // Polled quietly, each read waiting for the last: a newer read would otherwise overtake a slow one and throw it away.
  // A missed read is simply tried again next time.
  useEffect(() => {
    if (!running || !packageId) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = (): void => {
      timer = setTimeout(() => {
        void read(false)
          .catch(() => undefined)
          .then(() => {
            if (!stopped) poll();
          });
      }, NFA_BUILD_POLL_MS);
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [running, packageId, read]);

  return { status, loading, starting, running, load, start };
};
